import { useParams, Link, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import type { SatelliteIndex, Zone, SoilProfile, UserRole } from '@greeniq/shared';
import { api, ApiError } from '../api/client.js';
import { ZoneMiniMap } from '../components/ZoneMiniMap.js';
import { SoilTextureTriangle } from '../components/SoilTextureTriangle.js';
import { ZoneSatelliteCard } from '../components/ZoneSatelliteCard.js';
import { SatelliteTimeline } from '../components/SatelliteTimeline.js';
import { DeltaChips } from '../components/DeltaChips.js';
import { IndexExplainerDrawer } from '../components/IndexExplainerDrawer.js';
import { PhotoLogCard } from '../components/PhotoLogCard.js';
import { SensorLiveCard } from '../components/SensorLiveCard.js';
import { SensorTrendChart } from '../components/SensorTrendChart.js';
import { FracRotationCard } from '../components/FracRotationCard.js';
import { ZONE_TYPE_STYLE } from '../lib/zoneStyle.js';
import { useMe } from '../context/MeContext.js';

interface ZoneResponse {
  zone: Zone;
  soilProfiles: SoilProfile[];
}

const EDIT_ROLES: UserRole[] = ['ageye_admin', 'superintendent', 'assistant_super'];

export function ZoneDetailPage() {
  const { courseSlug, zoneId } = useParams();
  const navigate = useNavigate();
  const { me } = useMe();
  const [data, setData] = useState<ZoneResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [explainIndex, setExplainIndex] = useState<SatelliteIndex | null>(null);

  const role = me?.courses.find((c) => c.courseSlug === courseSlug)?.role;
  const canEdit = role ? EDIT_ROLES.includes(role) : false;

  async function load() {
    try {
      const resp = await api.get<ZoneResponse>(`/courses/${courseSlug}/zones/${zoneId}`);
      setData(resp);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    }
  }

  useEffect(() => {
    if (!courseSlug || !zoneId) return;
    void load();
  }, [courseSlug, zoneId]);

  async function refreshSoil() {
    if (!courseSlug || !zoneId) return;
    setRefreshing(true);
    try {
      await api.post<{ status: string }>(`/courses/${courseSlug}/zones/${zoneId}/refresh-soil`);
      await load();
    } catch (err) {
      alert(err instanceof ApiError ? err.message : String(err));
    } finally {
      setRefreshing(false);
    }
  }

  if (error) return <div className="p-8 text-[color:var(--color-error)]">{error}</div>;
  if (!data) return <div className="p-8 text-fg-muted">Loading…</div>;

  const { zone, soilProfiles } = data;
  const soil = soilProfiles[0];
  const style = ZONE_TYPE_STYLE[zone.zoneType];

  return (
    <div className="p-6 md:p-10 max-w-6xl">
      <Link to={`/app/${courseSlug}`} className="text-sm text-greeniq-400 hover:underline">
        ← Overview
      </Link>

      <div className="mt-3 flex items-start justify-between flex-wrap gap-4">
        <div>
          <p
            className="uppercase tracking-wider text-xs font-semibold"
            style={{ color: style.outline }}
          >
            {style.label}
          </p>
          <h1 className="mt-1 text-3xl md:text-4xl font-display font-bold text-fg">{zone.name}</h1>
          <p className="mt-1 text-fg-muted">
            {Math.round(Number(zone.areaSqft)).toLocaleString()} sqft
            {zone.grassSpecies ? ` · ${zone.grassSpecies}` : ''}
          </p>
        </div>
        {canEdit && (
          <button
            className="text-sm text-greeniq-400 hover:underline"
            onClick={() => navigate(`/app/${courseSlug}/map`)}
          >
            Edit shape →
          </button>
        )}
      </div>

      <div className="mt-8 grid gap-6 lg:grid-cols-2">
        <ZoneMiniMap zone={zone} />
        <div className="bg-bg-card rounded-lg border border-border p-5">
          <h2 className="text-sm uppercase tracking-wider text-fg-muted font-semibold">
            Soil Profile
          </h2>
          {soil ? (
            <div className="mt-3 flex flex-col md:flex-row gap-6 items-start">
              <SoilTextureTriangle
                sandPct={soil.sandPct}
                siltPct={soil.siltPct}
                clayPct={soil.clayPct}
                label={soil.usdaTexture ?? 'SSURGO'}
                size={220}
              />
              <div className="grid grid-cols-2 gap-3 flex-1">
                <SoilTile label="Texture" value={soil.usdaTexture ?? '—'} />
                <SoilTile label="Drainage" value={soil.drainageClass ?? '—'} />
                <SoilTile label="AWC" value={fmt(soil.awc, 2)} />
                <SoilTile label="Bulk density" value={fmt(soil.bulkDensity, 2)} />
                <SoilTile label="OM %" value={fmt(soil.omPct, 1)} />
                <SoilTile label="pH" value={fmt(soil.ph, 1)} />
                <SoilTile label="CEC" value={fmt(soil.cec, 1)} />
                <SoilTile label="Source" value="USDA WSS" />
              </div>
            </div>
          ) : (
            <div className="mt-3 text-sm text-fg-muted">
              <p>
                No USDA SSURGO profile cached. The zone centroid may be outside CONUS coverage, or
                the fetch is still in progress.
              </p>
              {canEdit && (
                <button
                  onClick={refreshSoil}
                  disabled={refreshing}
                  className="mt-3 px-3 py-1.5 text-xs rounded-md bg-greeniq-500 text-[#0b1014] font-semibold hover:bg-greeniq-400 disabled:opacity-50"
                >
                  {refreshing ? 'Fetching…' : 'Retry SSURGO fetch'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>

      {courseSlug && zoneId && (
        <>
          <ZoneSatelliteCard courseSlug={courseSlug} zoneId={Number(zoneId)} role={role} />
          <DeltaChips
            courseSlug={courseSlug}
            zoneId={Number(zoneId)}
            onExplain={setExplainIndex}
          />
          <SatelliteTimeline courseSlug={courseSlug} zoneId={Number(zoneId)} />
          <PhotoLogCard courseSlug={courseSlug} zoneId={Number(zoneId)} role={role} />
          <SensorLiveCard courseSlug={courseSlug} zoneId={Number(zoneId)} />
          <SensorTrendChart courseSlug={courseSlug} zoneId={Number(zoneId)} />
          <FracRotationCard courseSlug={courseSlug} zoneId={Number(zoneId)} />
          <IndexExplainerDrawer
            index={explainIndex}
            onClose={() => setExplainIndex(null)}
          />
        </>
      )}
      <DeferredSection
        title="Application log"
        phase="Phase 9"
        note="Every chemical + fertilizer + PGR applied to this zone, with FRAC rotation, REI/PHI tracking, nutrient accumulator."
      />
    </div>
  );
}

function SoilTile({ label, value }: { label: string; value: string }) {
  return (
    <div className="p-3 rounded-md bg-bg-elevated">
      <div className="text-xs uppercase tracking-wider text-fg-muted">{label}</div>
      <div className="mt-1 text-lg font-semibold text-greeniq-300">{value}</div>
    </div>
  );
}

function DeferredSection({
  title,
  phase,
  note,
}: {
  title: string;
  phase: string;
  note: string;
}) {
  return (
    <section className="mt-8 p-5 bg-bg-card rounded-lg border border-dashed border-border">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h2 className="text-sm uppercase tracking-wider text-fg-muted font-semibold">{title}</h2>
        <span className="text-xs px-2 py-0.5 rounded-full bg-greeniq-500/10 text-greeniq-400 uppercase tracking-wider">
          {phase}
        </span>
      </div>
      <p className="mt-2 text-sm text-fg-muted">{note}</p>
    </section>
  );
}

function fmt(v: number | null | undefined, digits: number) {
  if (v == null) return '—';
  const n = typeof v === 'number' ? v : Number(v);
  if (!Number.isFinite(n)) return '—';
  return n.toFixed(digits);
}
